/**
 * Browser-side release of the web push token. Needs NO login, so it also works when the session has already expired
 * (the server-side unlink needs a valid token and cannot). Deleting the FCM token makes this browser unreachable for
 * that user at once; the server row is retired automatically the next time it tries to send (token invalid).
 * Kept free of axios imports so the axios 401/403 handler can use it without an import cycle.
 */
export const LAST_TOKEN_KEY = "wolfpack_push_last_token";
const DELETE_TIMEOUT_MS = 2500;

let pending: Promise<void> | null = null;

const read = (): string | null => {
  try {
    return localStorage.getItem(LAST_TOKEN_KEY);
  } catch {
    return null;
  }
};

/** True when this browser registered a push token that has not been released yet. */
export const hasLocalPushToken = (): boolean => Boolean(read());

/** Resolves when any release in progress has finished. registerDevice awaits it so a quick re-login never races it. */
export const whenLocalPushReleased = (): Promise<void> => pending || Promise.resolve();

/** Best-effort: delete the FCM token and forget it locally. Never throws, never takes longer than ~2.5 s. */
export function releaseLocalPushToken(): Promise<void> {
  if (!hasLocalPushToken()) return Promise.resolve();
  if (pending) return pending;
  const work = (async () => {
    try {
      // Loaded lazily so importing this module (from the axios error handler) never initialises Firebase.
      const [{ getMessaging, deleteToken, isSupported }, { app }] = await Promise.all([import("firebase/messaging"), import("../config/firebase")]);
      if (await isSupported()) await deleteToken(getMessaging(app));
    } catch {
      /* best-effort */
    } finally {
      try {
        localStorage.removeItem(LAST_TOKEN_KEY);
      } catch {
        /* ignore */
      }
    }
  })();
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, DELETE_TIMEOUT_MS));
  pending = Promise.race([work, timeout]).finally(() => {
    pending = null;
  });
  return pending;
}
